import { defineStore } from 'pinia'
import { ref, watch } from 'vue'
import i18n from '@/locales'
import { LOCALE_STORAGE_KEY, normalizeLocale } from '@/utils/locale'

export const useLocaleStore = defineStore('locale', () => {
  const language = ref<string>(normalizeLocale(i18n.global.locale.value))

  const syncLanguage = (lang: string, persist = true) => {
    const normalized = normalizeLocale(lang)

    i18n.global.locale.value = normalized
    language.value = normalized
    document.documentElement.lang = normalized

    if (persist) {
      // 桌面端通过 StoreManager 持久化（跨窗口共享）
      if (window.electronAPI) {
        void window.electronAPI.storeSet('language', normalized)
      } else {
        // Web 端通过 localStorage 持久化
        localStorage.setItem(LOCALE_STORAGE_KEY, normalized)
      }
    }
  }

  // 监听 language ref 变化（支持 v-model 双向绑定自动持久化）
  watch(language, (newVal) => {
    const normalized = normalizeLocale(newVal)
    if (normalized !== i18n.global.locale.value) {
      syncLanguage(normalized)
    }
  })

  async function initLocale() {
    let saved: string | null = null

    if (window.electronAPI) {
      // 桌面端从主进程 StoreManager 读取
      saved = await window.electronAPI.storeGet('language')
    } else {
      // Web 端从 localStorage 读取
      saved = localStorage.getItem(LOCALE_STORAGE_KEY)
    }

    if (saved) {
      syncLanguage(saved)
    }
  }

  // 注册 IPC 监听：主进程推送语言变更（用于子窗口实时同步）
  // 注意：来自 IPC 的变更不写回 StoreManager，避免循环
  // onChange 可选回调，用于语言变更后执行额外操作（如刷新设备快照）
  function listenLocaleChange(onChange?: () => void): (() => void) | undefined {
    if (!window.electronAPI?.onLocaleChanged) return undefined
    return window.electronAPI.onLocaleChanged((lang: string) => {
      syncLanguage(lang, false)
      onChange?.()
    })
  }

  function t(key: string, named?: Record<string, unknown>) {
    return named ? i18n.global.t(key, named) : i18n.global.t(key)
  }

  return {
    language,
    syncLanguage,
    initLocale,
    listenLocaleChange,
    t,
  }
})
